import React, { useMemo } from 'react';
import {
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  LinearProgress,
  Checkbox,
  Box,
} from '@mui/material';
import { ContactPerson } from './ContactPerson';
import { LicenseAccess as SubscriptionAccess } from './LicenseAccess';
import { UsersInLicense } from './UsersInLicense';
import { ChangeLogCard } from './ChangeLogCard';
import { Orders } from '../../orders/Orders';
import Comments from './Comments';
import Tasks from '../../tasks/overview/Overview';
import { InfoComposite } from './InfoComposite';
import { Discounts } from './Discounts';
import { TestingPanel } from './TestingPanel';
import { Attachments } from './Attachments';
import { Notifications } from './Notifications';
import { useConfig } from '../../components/Configuration';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_LICENSE, START_SUSPEND_EMAL, END_SUSPEND_EMAIL, UPDATE_DOC_ACCESS } from './LicenseInfo.graphql';
import { License, LicenseModel, SingleDocumentAccessModel } from '../../interfaces';
import LoadingSpinner from '../../components/LoadingSpinner';
import { MainContentPanel } from '../../components/MainContentPanel';
import { formatDateOnly } from '../../Formatters';
import { GET_ACCESSES, STOP_ACCESSES } from '../../pdf/pdf.graphql';
import { Delete, Label } from '@mui/icons-material';
import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { GET_GUIDES } from '../../reports/reports.graphql';
import { Guide, GuideSelectionTableProps } from '../../interfaces';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Business, Assessment } from '@mui/icons-material';
import { Card, CardContent, CardActionArea } from '@mui/material';

// Memoized table row component for performance
// Memoized table row component for performance
const GuideTableRow = React.memo(
  ({
    guide,
    isSelected,
    selectedVersion,
    onGuideSelectionChange,
    onVersionChange,
  }: {
    guide: Guide;
    isSelected: boolean;
    selectedVersion: number | undefined;
    onGuideSelectionChange: (event: React.ChangeEvent<HTMLInputElement>, docId: number) => void;
    onVersionChange: (docId: number, versionId: number) => void;
  }) => {
    const hasVersions = guide.versions && guide.versions.length > 0;

    return (
      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
        <TableCell sx={{ width: '10%', textAlign: 'left' }}>{guide.docName}</TableCell>
        <TableCell sx={{ width: '50%', textAlign: 'left' }}>{guide.title}</TableCell>
        <TableCell sx={{ width: '30%', textAlign: 'left' }}>
          {hasVersions ? (
            <FormControl fullWidth size="small">
              <Select
                value={selectedVersion || ''}
                onChange={(event) => onVersionChange(guide.docId, Number(event.target.value))}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Velg versjon
                </MenuItem>
                {guide.versions.map((version) => (
                  <MenuItem key={version.versionId} value={version.versionId}>
                    {version.versionNumber} - {version.status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Ingen versjoner
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: '10%', textAlign: 'right' }}>
          <Checkbox
            checked={isSelected}
            onChange={(event) => onGuideSelectionChange(event, guide.docId)}
            disabled={!hasVersions || !selectedVersion}
          />
        </TableCell>
      </TableRow>
    );
  }
);

export default function Details() {
  const config = useConfig();
  const params = useParams<{ id: string }>();
  const licenseId = Number(params.id);
  const [suspendLicenseEmailStart, { loading: loadingStart }] = useMutation<any, any>(START_SUSPEND_EMAL, {
    variables: { input: { id: licenseId, noEmail: false } },
  });
  const [noEmailToUser, { loading: loadingNoEmail }] = useMutation<any, any>(START_SUSPEND_EMAL, { variables: { input: { id: licenseId, noEmail: true } } });
  const [suspendLicenseEmailEnd, { loading: loadingEnd }] = useMutation<any, any>(END_SUSPEND_EMAIL, { variables: { input: { id: licenseId } } });
  const { loading, data } = useQuery<{ license: { bksLicense: License; license: LicenseModel; isInvoicable: boolean } }>(GET_LICENSE, {
    variables: { id: licenseId },
  });

  const [selectedMenu, setSelectedMenu] = useState(0);
  if (data?.license.license?.emailSuspended && data?.license.license.noEmailToUser && selectedMenu === 0) {
    setSelectedMenu(2);
  }
  else if (data?.license.license?.emailSuspended && !data?.license.license.noEmailToUser && selectedMenu === 0) {
    setSelectedMenu(1);
  }



  if (loading) return <LoadingSpinner />;
  if (!data) return null;
  const license = data.license.license;
  const bksLicense = data.license.bksLicense;
  const isInvoicable = data.license.isInvoicable;
  var disableForm = false;
  if (loadingEnd || loadingStart || loadingNoEmail) disableForm = true;

  const handleChange = (e: any) => {
    if (e.target.value === 0) suspendLicenseEmailEnd();
    else if (e.target.value === 1) suspendLicenseEmailStart();
    else if (e.target.value === 2) noEmailToUser();

    setSelectedMenu(e.target.value);
  };

  function UsageReportCard({ bksLicense }: { bksLicense: License }) {
    return (
      <MainContentPanel>
        <Card elevation={1}>
          <CardActionArea component={RouterLink} to={`/reports/guides/company/${bksLicense.orgNumber}/${encodeURIComponent(bksLicense.companyName)}`}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
              <Assessment sx={{ fontSize: 32, marginRight: 2, color: 'primary.main' }} />
              <Typography variant="h6">Vis bruksrapport</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Business sx={{ color: 'text.secondary' }} />
            </CardContent>
          </CardActionArea>
        </Card>
      </MainContentPanel>
    );
  }

  return (
    <Grid container direction="row" sx={{alignItems: 'flex-start'}}>
      <Grid
        size={{
          md: 12,
          lg: 8
        }}>
        {<InfoComposite license={license} bksLicense={bksLicense} suspendValue={false} />}
      </Grid>
      <Grid
        size={{
          md: 12,
          lg: 4
        }}>
        <ContactPerson licenseId={licenseId} />
        {bksLicense.orgNumber && <UsageReportCard bksLicense={bksLicense} />}
      </Grid>
      <Grid
        size={{
          md: 12,
          lg: 3
        }}>
        <MainContentPanel>
          <FormControl variant="filled">
            <InputLabel id="selected_recipients-label">Epost</InputLabel>
            <Select
              labelId="selected_recipient-filled-label"
              id="selected_recipient-select-filled"
              value={selectedMenu}
              onChange={handleChange}
              disabled={disableForm}>
              <MenuItem value={0}>Vanlig epost</MenuItem>
              <MenuItem value={1}>Utsatt epost, samleliste</MenuItem>
              <MenuItem value={2}>Ingen epost</MenuItem>
            </Select>
          </FormControl>
        </MainContentPanel>
      </Grid>
      <Grid
        size={{
          md: 12,
          lg: 5
        }}>
        {<SubscriptionAccess license={license} bksLicense={bksLicense} suspendValue={false} />}
        <DocumentAccess bksLicense={bksLicense} isInvoicable={isInvoicable} />
      </Grid>
      <Grid
        size={{
          md: 12,
          lg: 4
        }}>
        <Discounts licenseId={licenseId} />
      </Grid>
      <Grid size={12}>
        <Tasks licenseId={licenseId} />
      </Grid>
      <Grid size={12}>
        <Orders licenseId={licenseId} />
      </Grid>
      <Grid size={12}>
        <Attachments licenseId={licenseId} />
      </Grid>
      <Grid size={12}>
        <Comments licenseId={licenseId} />
      </Grid>
      <Grid size={6}>
        <Notifications licenseId={licenseId} />
      </Grid>
      <Grid size={6}>
        <ChangeLogCard licenseId={licenseId} />
      </Grid>
      {config && config.featureToggleTesting && (
        <Grid size={6}>
          <TestingPanel licenseId={licenseId} />
        </Grid>
      )}
      <Grid size={12}>
        <UsersInLicense licenseId={licenseId} />
      </Grid>
    </Grid>
  );
}

function DocumentAccess({ bksLicense, isInvoicable }: { bksLicense: License; isInvoicable: boolean }) {
  const { data, loading, refetch } = useQuery<{ accesses: SingleDocumentAccessModel[] }>(GET_ACCESSES, { variables: { licenseId: bksLicense.id } });

  const [stopAccess, { loading: stopAccessLoading }] = useMutation<any, any>(STOP_ACCESSES);
  let userId: number | undefined;

  if (!loading && data && data.accesses.length > 0) {
    userId = data.accesses[0].userId;
  } else {
    userId = bksLicense.contactId;
  }
  const onStopAccess = (docId: number, versionId: number) => {
    handleOpenDialog({ docId, versionId });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedListItem, setSelectedListItem] = useState<{ docId: number; versionId: number } | null>(null);

  const [isDocListDialogOpen, setIsDocListDialogOpen] = useState(false);

  const handleOpenDocListDialog = () => {
    setIsDocListDialogOpen(true);
  };

  const handleCloseDocListDialog = () => {
    setIsDocListDialogOpen(false);
    refetch();
  };

  const handleOpenDialog = (item: { docId: number; versionId: number }) => {
    setSelectedListItem(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedListItem(null);
    setDialogOpen(false);
  };

  const handleConfirmAction = async () => {
    try {
      await stopAccess({ variables: { input: { licenseId: bksLicense.id, docId: selectedListItem?.docId, versionId: selectedListItem?.versionId } } });
      await refetch();
      handleCloseDialog();
    } catch (error) {
      console.error(error);
      handleCloseDialog();
      await refetch();
    }
  };

  const handleDialogClose = () => {
    handleCloseDocListDialog();
    handleCloseDialog();
  };

  return (
    <MainContentPanel>
      <Typography gutterBottom variant="h5">
        Tilgang til enkeltanvisninger
        <IconButton title="Gi tilgang til enkeltanvisninger" onClick={() => handleOpenDocListDialog()} size="large">
          <Add />
        </IconButton>
      </Typography>
      {loading && <LoadingSpinner />}
      {data && (
        <>
          <List dense>
            {data &&
              data?.accesses &&
              data.accesses.map((access, index) => (
                <ListItem key={`${access.id}`}>
                  <ListItemText
                    primary={`${access.docName} ${access.title}`}
                    secondary={`Fra ${formatDateOnly(access.validFrom)} til ${formatDateOnly(access.validTo)}`}
                  />
                  {access.guideVersionPublicId && (
                    <IconButton size="small" title={`Versjon: ${access.guideVersionPublicId}`} sx={{ color: 'teal' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        v {access.guideVersionPublicId}
                      </Typography>
                    </IconButton>
                  )}
                  <ListItemSecondaryAction title="Avslutt tilgangen" onClick={() => onStopAccess(access.docId, access.versionId)}>
                    <IconButton>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
          </List>
        </>
      )}
      <Dialog open={isDocListDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          Gi tilgan til enkeltanvisninger
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginRight: (theme) => theme.spacing(1),
              marginBottom: (theme) => theme.spacing(1),
            }}>
            <Button onClick={handleCloseDocListDialog}>Lukke</Button>
          </DialogActions>
        </DialogTitle>

        <DialogContent>
          <DocumentList
            bksLicense={bksLicense}
            userId={userId}
            existingDocument={data?.accesses}
            closeCallback={handleCloseDocListDialog}
            refetchCallback={refetch}
          />
        </DialogContent>
      </Dialog>
      <Dialog key={dialogOpen.toString()} open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Advarsel</DialogTitle>
        <DialogContent>
          <Typography>Er du sikker på at du vil fjerne tilgang?</Typography>
          {stopAccessLoading && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Nei</Button>
          <Button onClick={handleConfirmAction} disabled={stopAccessLoading}>
            Ja
          </Button>
        </DialogActions>
      </Dialog>
    </MainContentPanel>
  );
}

function DocumentList({
  bksLicense,
  userId,
  existingDocument,
  closeCallback,
  refetchCallback,
}: {
  bksLicense: License;
  userId: number | undefined;
  existingDocument: SingleDocumentAccessModel[] | undefined;
  closeCallback: () => void;
  refetchCallback: () => void;
}) {
  return (
    <MainContentPanel>
      <GuideSelectionTable licenseId={bksLicense.id} guides={[]} userId={userId} closeCallback={closeCallback} retechCallback={refetchCallback} />
    </MainContentPanel>
  );
}

import { Pagination } from '@mui/material';

function GuideSelectionTable({ licenseId, guides, userId, closeCallback, retechCallback }: GuideSelectionTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getOneYearAfterDate());
  const [selectedGuides, setSelectedGuides] = useState<number[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<Record<number, number>>({});
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  function getTodayDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getOneYearAfterDate() {
    const today = new Date();
    today.setFullYear(today.getFullYear() + 1);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const [updateDocAccess, { loading: loadingStart }] = useMutation<any, any>(UPDATE_DOC_ACCESS);

  // Fetch guides with pagination and search from backend
  const { data: guidesData, loading: guidesLoading } = useQuery<any, any>(GET_GUIDES, {
    variables: {
      page: page,
      pageSize: itemsPerPage,
      searchTerm: debouncedSearchTerm || null,
    },
    fetchPolicy: 'network-only',
  });

  const totalPages = guidesData?.allGuides?.totalCount ? Math.ceil(guidesData.allGuides.totalCount / itemsPerPage) : 0;
  const displayedGuides = guidesData?.allGuides?.guides || [];

  // Memoize handlers
  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handlePageChange = React.useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handleFromDateChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  }, []);

  const handleToDateChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  }, []);

  const handleVersionChange = React.useCallback((docId: number, versionId: number) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [docId]: versionId,
    }));
  }, []);

  const handleGuideSelectionChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, docId: number) => {
    const selected = event.target.checked;
    setSelectedGuides((prevSelectedGuides) => {
      if (selected) {
        return [...prevSelectedGuides, docId];
      } else {
        setSelectedVersions((prev) => {
          const newVersions = { ...prev };
          delete newVersions[docId];
          return newVersions;
        });
        return prevSelectedGuides.filter((id) => id !== docId);
      }
    });
  }, []);

  const handleSave = async () => {
    const guidesString = selectedGuides.map((docId) => `${docId}:${selectedVersions[docId]}`).join('-');

    await updateDocAccess({
      variables: {
        input: {
          licenseId: licenseId,
          guides: guidesString,
          userId: userId,
          fromDate: fromDate,
          toDate: toDate,
        },
      },
    });
    closeCallback();
    retechCallback();
  };

  const isButtonDisabled =
    selectedGuides.length === 0 || !fromDate || !toDate || fromDate >= toDate || selectedGuides.some((docId) => !selectedVersions[docId]);

  return (
    <>
      {(loadingStart || guidesLoading) && <LinearProgress />}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={12}>
          <TextField
            label="Søk i alle anvisninger"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            placeholder="Søk etter nummer eller tittel..."
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField label="Fra dato" type="date" value={fromDate} onChange={handleFromDateChange} fullWidth  slotProps={{inputLabel: { shrink: true }}}/>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField label="Til dato" type="date" value={toDate} onChange={handleToDateChange} fullWidth  slotProps={{inputLabel: { shrink: true }}}/>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="caption" color="textSecondary">
          {debouncedSearchTerm
            ? `Viser ${displayedGuides.length} av ${guidesData?.allGuides?.totalCount || 0} søkeresultater (side ${page} av ${totalPages})`
            : `Viser ${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, guidesData?.allGuides?.totalCount || 0)} av ${guidesData?.allGuides?.totalCount || 0
            } anvisninger`}
        </Typography>
        {selectedGuides.length > 0 && (
          <Typography variant="caption" color="primary" sx={{fontWeight: 'bold'}}>
            {selectedGuides.length} valgt
          </Typography>
        )}
      </Box>
      <Box>
        <div style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ width: '10%', textAlign: 'left' }}>Nummer</TableCell>
                <TableCell sx={{ width: '50%', textAlign: 'left' }}>Tittel</TableCell>
                <TableCell sx={{ width: '30%', textAlign: 'left' }}>Versjon</TableCell>
                <TableCell sx={{ width: '10%', textAlign: 'left' }}>Velg</TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </div>

        <div style={{ height: '600px', overflow: 'auto', minWidth: '650px' }}>
          <Table style={{ tableLayout: 'fixed' }}>
            <TableBody>
              {displayedGuides.map((guide) => (
                <GuideTableRow
                  key={guide.docId}
                  guide={guide}
                  isSelected={selectedGuides.includes(guide.docId)}
                  selectedVersion={selectedVersions[guide.docId]}
                  onGuideSelectionChange={handleGuideSelectionChange}
                  onVersionChange={handleVersionChange}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </Box>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton size="large" />
        </Box>
      )}
      <Grid container spacing={2} sx={{marginTop: '20px', justifyContent: 'flex-end'}}>
        <Grid>
          <Button onClick={closeCallback}>Avbryt</Button>
        </Grid>
        <Grid>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isButtonDisabled}>
            LAGRE
          </Button>
        </Grid>
      </Grid>
    </>
  );
}